/**
 * Lista de sugestões que aparece ao digitar arroba no campo de escrita.
 *
 * Só desenha: quem manda nas setas e no Enter é o campo de texto, que é onde o
 * foco está. Tirar o foco do campo para navegar na lista quebraria o hábito de
 * escrever sem soltar o teclado.
 */

import { useEffect, useRef } from 'react'
import type { GuildMember, PresenceStatus } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { Avatar } from '@/components/ui'

export function MentionAutocomplete({
  membros,
  ativo,
  presencas,
  onEscolher,
  onPassarPorCima,
}: {
  membros: GuildMember[]
  /** Índice destacado, controlado pelas setas do campo de texto. */
  ativo: number
  presencas: Record<string, { status: PresenceStatus } | undefined>
  onEscolher: (membro: GuildMember) => void
  onPassarPorCima: (indice: number) => void
}) {
  const listaRef = useRef<HTMLDivElement>(null)

  // Mantém o item destacado visível quando a navegação passa do que cabe.
  useEffect(() => {
    const item = listaRef.current?.querySelector<HTMLElement>(`[data-indice="${ativo}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [ativo])

  if (membros.length === 0) return null

  return (
    <div
      role="listbox"
      aria-label="Sugestões de menção"
      className="absolute right-4 bottom-full left-4 z-40 mb-1 overflow-hidden rounded-lg bg-surface-overlay shadow-2xl animate-fade-in"
    >
      <div className="px-3 py-2 text-[11px] font-bold tracking-wider text-content-muted uppercase">
        Membros
      </div>

      <div ref={listaRef} className="max-h-64 overflow-y-auto px-1.5 pb-1.5">
        {membros.map((membro, indice) => {
          const nome = membro.nickname ?? membro.user.displayName

          return (
            <button
              key={membro.userId}
              type="button"
              data-indice={indice}
              // O clique precisa do mousedown: o mouseup depois de o campo
              // perder o foco fecharia a lista antes de a escolha chegar.
              onMouseDown={(evento) => {
                evento.preventDefault()
                onEscolher(membro)
              }}
              onMouseEnter={() => onPassarPorCima(indice)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors',
                indice === ativo ? 'bg-surface-active' : 'hover:bg-surface-hover',
              )}
            >
              <Avatar
                userId={membro.userId}
                name={nome}
                src={membro.user.avatarUrl}
                size="xs"
                status={presencas[membro.userId]?.status ?? 'offline'}
              />
              <span className="truncate text-[14px] font-medium text-content-primary">{nome}</span>
              <span className="truncate text-[13px] text-content-muted">
                {membro.user.username}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
